import { checkIfExtraModification } from '../modifications';

import type { Modification, ModificationsMap } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Prepares active modifications for UI display by merging extras quantities
 * into their paired included entries.
 *
 * - Extras with a paired included modification are removed from the list and
 *   their quantity is folded into the matching included modification.
 * - Extras without a selected included counterpart are kept as standalone entries.
 */
export function mergeExtrasIntoActiveModifications(params: Params): Modification[] {
  const { activeModifications, includedModificationsMap } = params;

  // ─────────────────────────────────────────────────────────────────────

  const accumulateExtrasQuantity = accumulateExtrasQuantityFactory(includedModificationsMap);
  const extraModifications = activeModifications.filter(checkIfExtraModification);
  const extrasQuantitiesByIncludedId = extraModifications.reduce(accumulateExtrasQuantity, {});

  // ─────────────────────────────────────────────────────────────────────

  const mergeModification = mergeModificationFactory(extrasQuantitiesByIncludedId);
  const mergedModificationsMap = activeModifications.reduce(mergeModification, new Map());

  // ─────────────────────────────────────────────────────────────────────

  const checkIfUnpairedExtra = checkIfUnpairedExtraFactory(
    mergedModificationsMap,
    includedModificationsMap,
  );
  const remapToIncluded = remapToIncludedFactory(includedModificationsMap);

  const unpairedExtras = extraModifications.filter(checkIfUnpairedExtra).map(remapToIncluded);

  // ─────────────────────────────────────────────────────────────────────

  return [...mergedModificationsMap.values(), ...unpairedExtras];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function accumulateExtrasQuantityFactory(includedModificationsMap: ModificationsMap) {
  return function (quantities: Record<string, number>, modification: Modification) {
    if (!modification.ingredientId) return quantities;

    const includedModification = includedModificationsMap.get(modification.ingredientId);

    if (!includedModification?.ingredient) return quantities;

    const includedId = includedModification.ingredient.id;

    return {
      ...quantities,
      [includedId]: (quantities[includedId] ?? 0) + modification.quantity,
    };
  };
}

function mergeModificationFactory(extrasQuantitiesByIncludedId: Record<string, number>) {
  return function (map: Map<string, Modification>, modification: Modification) {
    if (modification.isExtra) return map;

    const ingredientId = modification.ingredient?.id;

    if (!ingredientId) return map;

    const extrasQuantity = extrasQuantitiesByIncludedId[ingredientId] ?? 0;
    const merged =
      extrasQuantity > 0
        ? { ...modification, quantity: modification.quantity + extrasQuantity }
        : modification;

    map.set(ingredientId, merged);

    return map;
  };
}

/**
 * Remaps an unpaired extras modification to its included counterpart's identity
 * so the UI can look it up by the included modification's ID.
 */
function remapToIncludedFactory(includedModificationsMap: ModificationsMap) {
  return function (modification: Modification): Modification {
    if (!modification.ingredientId) return modification;

    const includedModification = includedModificationsMap.get(modification.ingredientId);

    if (!includedModification) return modification;

    return { ...includedModification, quantity: modification.quantity };
  };
}

function checkIfUnpairedExtraFactory(
  mergedModificationsMap: Map<string, Modification>,
  includedModificationsMap: ModificationsMap,
) {
  return function (modification: Modification) {
    if (!modification.ingredientId) return true;

    const includedModification = includedModificationsMap.get(modification.ingredientId);

    if (!includedModification?.ingredient) return true;

    return !mergedModificationsMap.has(includedModification.ingredient.id);
  };
}

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = {
  activeModifications: Modification[];
  includedModificationsMap: ModificationsMap;
};
